import { setPageMeta } from "@/lib/seo";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { PageCTA } from "@/components/wve/PageCTA";

const spaces = [
  {
    n: "01",
    name: "Photo Studio",
    img: "https://static.wixstatic.com/media/1fe187_a40e7af24afc4371b04edde0f8e01992~mv2.png/v1/fill/w_649,h_735,al_c,q_90/photo.png",
    desc: "A 3m × 3m white cyclorama wall with full studio lighting and 12 themed backdrops — perfect for casting headshots, family portraits, modelling and portfolio shoots. Our in-house photographer will put you at ease.",
    tags: ["3m × 3m cyclorama", "12 themed backdrops", "Studio lighting", "In-house photographer"],
  },
  {
    n: "02",
    name: "Video Studio",
    img: "https://static.wixstatic.com/media/1fe187_94683d4f534141088e6cc5090fdc43f2~mv2.png/v1/fill/w_628,h_735,al_c,q_90/video.png",
    desc: "Our versatile Live Lounge style set, captured from multiple angles. Stage lighting, backdrop holders and impressive upload speeds — ready for anything from showreels to full live broadcasts.",
    tags: ["Live Lounge set", "Multi-angle capture", "Stage lighting"],
  },
  {
    n: "03",
    name: "Podcast Studio",
    img: "https://static.wixstatic.com/media/1fe187_66a1cb15eb0248c7ace2cb1ac6632108~mv2.png/v1/fill/w_628,h_735,al_c,q_90/podcast.png",
    desc: "A purpose-built space designed for creators, storytellers and content pros. Four broadcast-quality mics, three HD cameras, sound-treated walls, a large TV for branding or playback, and custom lighting effects.",
    tags: ["4 broadcast mics", "3 HD cameras", "Sound-treated walls", "Custom lighting"],
  },
];

const offers = [
  {
    price: "£45",
    title: "Podcast Recording",
    desc: "Everything you need to record high-quality podcasts, interviews or video content — four broadcast-quality mics, three HD cameras, sound-treated walls and custom lighting effects.",
  },
  {
    price: "£50",
    title: "Photoshoots",
    desc: "Casting headshots, family portraits, modelling and updated portfolio shoots on our 3m × 3m white cyclorama wall. Our experienced photographer assists with poses and shows you examples as you go.",
  },
  {
    price: "£200",
    title: "Live Lounge Video",
    desc: "Perform your latest original material or favourite covers in our Live Lounge set, captured on multiple angles. Broadcast live to social media — or take the footage away to upload yourself. Bring your instrument, even if that's only your voice!",
  },
  {
    price: "£399",
    title: "Artist Showreels",
    desc: "A three, four or five-track showreel recorded in a 4-hour half-day session, with lighting backdrops, Special FX and high-end video & audio equipment. Ready to impress agents, venues and fans.",
  },
  {
    price: "£500",
    title: "Live Broadcasts & Streams",
    desc: "Seminars, classes, product launches, award presentations, meetings, live performances, TV shows and concerts — with impressive upload speeds so you always stay connected to your audience.",
  },
];

export default function StudioPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta(
      "Studio Hire Wickford — Photo, Video & Podcast Studios | WVE Studios",
      "Hire WVE Studios' three studios in Wickford, Essex: 3m cyclorama photo studio, Live Lounge video studio and sound-treated podcast suite. Sessions from £45.",
      "/studios"
    );
  }, []);

  return (
    <main data-testid="studio-page">
      <section className="bg-[#0A0A0A] text-white pt-32 sm:pt-40 pb-20 relative overflow-hidden">
        <span className="absolute -right-16 top-0 font-display text-[20rem] leading-none text-white/[0.05] select-none hidden lg:block">WVE</span>
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 relative">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="text-sm uppercase tracking-[0.3em] font-semibold text-[#E80042] mb-4">The Studios — Wickford, Essex</p>
            <h1 className="font-display uppercase leading-[0.95] text-5xl sm:text-7xl lg:text-8xl tracking-tight">
              Three studios.<br /><span className="text-[#E80042]">One roof.</span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed">
              Photo, video & podcast studios at Unit 6 Hodgson Court, Wickford — fully equipped, fully crewed and ready to hire from just £45.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="studio-spaces-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-6">
            {spaces.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                className={`group ${i === 1 ? "md:mt-12" : ""}`}
                data-testid={`studio-space-${s.n}`}
              >
                <div className="relative overflow-hidden rounded-lg">
                  <img loading="lazy" src={s.img} alt={s.name} className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-5 left-5 bg-[#E80042] text-white font-display text-lg px-4 py-1 rounded-full">{s.n}</span>
                </div>
                <h3 className="font-display uppercase text-3xl mt-6 tracking-tight">{s.name}</h3>
                <p className="text-[#555555] leading-relaxed mt-3">{s.desc}</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {s.tags.map((t) => (
                    <span key={t} className="border border-black/15 rounded-full px-3 py-1 text-xs uppercase tracking-[0.12em] font-semibold">
                      {t}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-[#0A0A0A] text-white" data-testid="studio-pricing-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="mb-14">
            <p className="text-sm uppercase tracking-[0.3em] font-semibold text-[#E80042] mb-4">Studio Hire</p>
            <h2 className="font-grotesk font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
              What you can shoot <span className="text-[#E80042]">here.</span>
            </h2>
          </motion.div>
          <div className="divide-y divide-white/10 border-y border-white/10">
            {offers.map((o, i) => (
              <motion.div
                key={o.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="grid sm:grid-cols-12 gap-4 py-10 group"
                data-testid={`studio-offer-${i + 1}`}
              >
                <div className="sm:col-span-3">
                  <span className="text-xs uppercase tracking-[0.25em] text-white/50 block">From</span>
                  <span className="font-display text-5xl sm:text-6xl text-[#E80042]">{o.price}</span>
                </div>
                <div className="sm:col-span-9">
                  <h3 className="font-display uppercase text-2xl sm:text-3xl tracking-tight group-hover:translate-x-2 transition-transform duration-300">{o.title}</h3>
                  <p className="text-white/70 leading-relaxed mt-3 max-w-3xl">{o.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <PageCTA context="Studio Hire" title="Book a studio." subtitle="Unit 6 Hodgson Court, Hodgson Way, Wickford, Essex, SS11 8XR" />
    </main>
  );
}
